import type { TaskRecord, TaskStatus } from '@shared/types';

export const taskStatusOrder: TaskStatus[] = ['backlog', 'todo', 'in_progress', 'done', 'archived'];

function normalizeBoardOrder(value: number, length: number): number {
  return Math.max(0, Math.min(length, value));
}

function applyTaskStatusTransition(task: TaskRecord, targetStatus: TaskStatus, changedAt: string): TaskRecord {
  const nextTask: TaskRecord = {
    ...task,
    status: targetStatus,
    updatedAt: changedAt
  };

  if (targetStatus === 'in_progress' && !nextTask.startedAt) {
    nextTask.startedAt = changedAt;
  }

  if (targetStatus === 'done') {
    nextTask.completedAt = changedAt;
  } else if (task.status === 'done') {
    nextTask.completedAt = null;
  }

  if (targetStatus === 'archived') {
    nextTask.archivedAt = changedAt;
  } else if (task.status === 'archived') {
    nextTask.archivedAt = null;
  }

  return nextTask;
}

function reindexColumnTasks(tasks: TaskRecord[], changedAt: string): TaskRecord[] {
  return tasks.map((task, index) => ({
    ...task,
    boardOrder: index,
    updatedAt: task.boardOrder === index ? task.updatedAt : changedAt
  }));
}

export function sortTasksByBoardOrder(tasks: TaskRecord[]): TaskRecord[] {
  return [...tasks].sort((left, right) => {
    if (left.boardOrder !== right.boardOrder) {
      return left.boardOrder - right.boardOrder;
    }

    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
  });
}

export function placeTask(
  tasks: TaskRecord[],
  options: {
    taskId: string;
    targetStatus: TaskStatus;
    targetBoardOrder: number;
    changedAt: string;
  }
): TaskRecord[] {
  const task = tasks.find((item) => item.id === options.taskId);

  if (!task) {
    throw new Error(`Task ${options.taskId} not found.`);
  }

  if (task.status === options.targetStatus && task.boardOrder === options.targetBoardOrder) {
    return tasks;
  }

  if (task.status === options.targetStatus) {
    const columnTasks = sortTasksByBoardOrder(tasks.filter((item) => item.status === task.status && item.id !== task.id));
    const nextTask = applyTaskStatusTransition(task, options.targetStatus, options.changedAt);

    columnTasks.splice(normalizeBoardOrder(options.targetBoardOrder, columnTasks.length), 0, nextTask);

    const reindexedColumn = reindexColumnTasks(columnTasks, options.changedAt);
    const replacements = new Map(reindexedColumn.map((item) => [item.id, item]));

    return tasks.map((item) => replacements.get(item.id) ?? item);
  }

  const sourceColumn = sortTasksByBoardOrder(tasks.filter((item) => item.status === task.status && item.id !== task.id));
  const targetColumn = sortTasksByBoardOrder(tasks.filter((item) => item.status === options.targetStatus));
  const nextTask = applyTaskStatusTransition(task, options.targetStatus, options.changedAt);

  targetColumn.splice(normalizeBoardOrder(options.targetBoardOrder, targetColumn.length), 0, nextTask);

  const reindexedSource = reindexColumnTasks(sourceColumn, options.changedAt);
  const reindexedTarget = reindexColumnTasks(targetColumn, options.changedAt);
  const replacements = new Map([...reindexedSource, ...reindexedTarget].map((item) => [item.id, item]));

  return tasks.map((item) => replacements.get(item.id) ?? item);
}
